import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { ContentEditable } from '@lexical/react/LexicalContentEditable';
import LexicalErrorBoundary from '@lexical/react/LexicalErrorBoundary';
import { HistoryPlugin } from '@lexical/react/LexicalHistoryPlugin';
import { RichTextPlugin } from '@lexical/react/LexicalRichTextPlugin';
import { TablePlugin } from '@lexical/react/LexicalTablePlugin';
import { Box, useTheme } from '@mui/material';
import debounce from 'lodash/debounce';
import { CcfSparklesIcon, useTranslator } from '@nice-ccf/ui-controls';
import { useEffect, useState } from 'react';
import { $createParagraphNode, $createTextNode, $getRoot } from 'lexical';
import { CcfEditorToolbar } from './ccf-lexical-toolbar';
import ccfRichTextEditorStyles, {
  richTextEditorCSS,
} from './ccf-rich-text-editor.styles';
import { useSelector } from 'react-redux';
import EditorPlaceholder from './ccf-editor-placeholder';
import {
  getCopilotEmailRequestStatus,
  getIsEditorActionPerformed,
} from '../ccf-app-space/ccf-copilot/ccf-copilot-chat/+state/ccf-live-copilot.slice';
import {
  activeContactSelector,
  setIsEmailResponseLoadedInEditor,
} from '../ccf-inbox/ccf-inbox.slice';
import { ListPlugin } from '@lexical/react/LexicalListPlugin';
import { LinkPlugin } from '@lexical/react/LexicalLinkPlugin';
import { useLexicalIsTextContentEmpty } from './ccf-is-text-content-empty-hook';
import CcfInlineImagePlugin from './ccf-inline-image-plugin';
import CcfAttachmentContainer from '../ccf-attachment-container/ccf-fileupload';

/**
 * Component used for lexical editor
 * @param props - LexicalEditorContent
 * @example <LexicalEditorContent />
 * @returns
 */
const LexicalEditorContent = ({
  generatedEmailResponse,
  setEmailSendButton,
  editorRef,
  handleTextSelection,
  dispatch,
  fileUpload,
  getFileInfo,
}: {
  generatedEmailResponse: string[];
  setEmailSendButton: (value: boolean) => void;
  editorRef: React.MutableRefObject<any>;
  handleTextSelection: () => void;
  dispatch: any;
  fileUpload?: React.MutableRefObject<any>;
  getFileInfo?: (file: any) => void;
}) => {
  const [editor] = useLexicalComposerContext();
  const theme = useTheme();
  const styles = ccfRichTextEditorStyles(theme);
  const activecontact = useSelector(activeContactSelector);
  const [isResponseLoading, setResponseLoading] = useState<boolean>(false);
  const isCopilotGeneratingEmailResponse = useSelector(
    getCopilotEmailRequestStatus(activecontact?.id || '')
  );
  const isCopilotEditorActionPerformed = useSelector(
    getIsEditorActionPerformed(activecontact?.id || '')
  );
  const [translate] = useTranslator();
  const richTextEditorStyles = richTextEditorCSS(theme);
  const isEditorEmpty = useLexicalIsTextContentEmpty(editor);

  useEffect(() => {
    console.log(
      'isCopilotGeneratingEmailResponse:',
      isCopilotGeneratingEmailResponse
    );
    console.log(
      'isCopilotEditorActionPerformed:',
      isCopilotEditorActionPerformed
    );

    const updateResponseLoading = debounce(() => {
      setResponseLoading(
        isCopilotGeneratingEmailResponse || isCopilotEditorActionPerformed
      );
    }, 300);

    updateResponseLoading();

    if (isCopilotGeneratingEmailResponse || isCopilotEditorActionPerformed) {
      const timer = setTimeout(() => {
        setResponseLoading(false);
        dispatch(setIsEmailResponseLoadedInEditor(true));
      }, 10000);
      return () => {
        clearTimeout(timer);
        dispatch(setIsEmailResponseLoadedInEditor(false));
        updateResponseLoading.cancel();
      };
    }
    return () => updateResponseLoading.cancel();
  }, [isCopilotGeneratingEmailResponse, isCopilotEditorActionPerformed]);

  useEffect(() => {
    if (editorRef && editor) {
      editorRef.current = editor;
      setEmailSendButton(true);
    }
  }, [editorRef, editor]);

  useEffect(() => {
    if (generatedEmailResponse?.length && editor) {
      editor.update(() => {
        const root = $getRoot();
        root.clear();
        generatedEmailResponse.forEach((response: any) => {
          const paragraphNode = $createParagraphNode();
          const textNode = $createTextNode(response);
          paragraphNode.append(textNode);
          root.append(paragraphNode);
        });
      });
      setEmailSendButton(true);
    }
  }, [generatedEmailResponse, editor]);

  /**
   * getEditorBodyStyles method to add styling for the rich editor body
   * @example getEditorBodyStyles();
   * @returns style class
   */
  const getEditorBodyStyles = () => {
    const baseStyles = {
      ...styles.editorBody,
    };
    if (isResponseLoading) {
      return {
        ...baseStyles,
        height: '100%',
        [theme.breakpoints.down('xl')]: {
          height: '100%',
        },
        overflow: 'hidden',
      };
    }
    return baseStyles;
  };

  useEffect(() => {
    setEmailSendButton(!isEditorEmpty);
  }, [isEditorEmpty]);

  return (
    <div>
      <style>{richTextEditorStyles}</style>
      <RichTextPlugin
        contentEditable={
          <Box sx={{ ...styles.editorContainer }} ref={editorRef.current}>
            <Box sx={getEditorBodyStyles()} id="richTextEditorBody">
              {isResponseLoading ? (
                <EditorPlaceholder
                  placeholderLoadingIcon={
                    <CcfSparklesIcon
                      htmlColor={theme?.palette?.primary?.main}
                      sx={styles.sparklesIcon}
                      data-testid="sparkles-icon"
                    />
                  }
                  loadingText={translate(
                    'copilotEmailGenerationPlaceholderText'
                  )} //: translate('copilotEmailUpdatingPlaceholderText')}
                />
              ) : (
                <ContentEditable
                  className="ccfRichEditor"
                  spellCheck={true}
                  style={styles.editorBody.contentEdit}
                />
              )}
              <CcfAttachmentContainer/>
            </Box>
          </Box>
        }
        ErrorBoundary={LexicalErrorBoundary}
        placeholder={null}
      />
      <ListPlugin />
      <LinkPlugin />
      <CcfEditorToolbar editor={editor} fileUpload={fileUpload} getFileInfo={getFileInfo} />
      <HistoryPlugin />
      <TablePlugin />
      <CcfInlineImagePlugin />
    </div>
  );
};

export default LexicalEditorContent;
